import { z } from "zod";

// ── Name sanitization rules (admin blocklist) ──

/**
 * One entry of the name-cleaning blocklist. `character` entries are single
 * characters removed on sight; `title` entries are whole tokens removed when they
 * stand alone ("PhD", "Dr."). Two kinds in one shape because they are edited on
 * the same screen and read in the same breath.
 */
export const NameSanitizationRuleKindSchema = z.enum(["character", "title"]);
export type NameSanitizationRuleKind = z.infer<typeof NameSanitizationRuleKindSchema>;

export const NameSanitizationRuleSchema = z.object({
    id: z.uuid(),
    kind: NameSanitizationRuleKindSchema,
    value: z.string(),
    created_at: z.string(),
});
export type NameSanitizationRule = z.infer<typeof NameSanitizationRuleSchema>;

export const NameSanitizationRuleListResponseSchema = z.array(NameSanitizationRuleSchema);
export type NameSanitizationRuleListResponse = z.infer<
    typeof NameSanitizationRuleListResponseSchema
>;

// A `character` entry is one character on purpose: the engine compares characters,
// not substrings, so a two-character entry could never match anything.
export const CreateNameSanitizationRuleRequestSchema = z.object({
    kind: NameSanitizationRuleKindSchema,
    value: z.string().trim().min(1).max(32),
});
export type CreateNameSanitizationRuleRequest = z.infer<
    typeof CreateNameSanitizationRuleRequestSchema
>;

// ── Preview ──

// What the settings screen's test field sends. Saving nothing, writing nothing:
// it exists so an admin can see what a rule change does BEFORE committing to a
// database-wide run they cannot undo.
export const PreviewNameSanitizationRequestSchema = z.object({
    name: z.string().max(200),
});
export type PreviewNameSanitizationRequest = z.infer<typeof PreviewNameSanitizationRequestSchema>;

export const PreviewNameSanitizationResponseSchema = z.object({
    original: z.string(),
    cleaned: z.string(),
    changed: z.boolean(),
});
export type PreviewNameSanitizationResponse = z.infer<
    typeof PreviewNameSanitizationResponseSchema
>;

// ── Clearing a whole list ──

/**
 * How many rules the clear removed.
 *
 * Returned rather than assumed: an admin who clears a list they believed held
 * five entries and is told nine were removed has learned something.
 */
export const ClearNameSanitizationRulesResponseSchema = z.object({
    deleted: z.number().int(),
});
export type ClearNameSanitizationRulesResponse = z.infer<
    typeof ClearNameSanitizationRulesResponseSchema
>;

// ── Retroactive run ──

/**
 * What the on-demand database pass did.
 *
 * `skipped_admin_edited` is not decoration: the pass never touches a profile whose
 * name an admin corrected by hand, so without that figure a run over a
 * hand-curated base reads as broken: "0 cleaned" facing names the admin can see
 * are still dirty.
 */
export const RunNameCleaningResponseSchema = z.object({
    scanned: z.number().int(),
    cleaned: z.number().int(),
    unchanged: z.number().int(),
    skipped_admin_edited: z.number().int(),
});
export type RunNameCleaningResponse = z.infer<typeof RunNameCleaningResponseSchema>;

// ── HTTP envelopes ({ success, data }) ──

export const NameSanitizationRuleListDetailResponseSchema = z.object({
    success: z.boolean(),
    data: NameSanitizationRuleListResponseSchema,
});
export type NameSanitizationRuleListDetailResponse = z.infer<
    typeof NameSanitizationRuleListDetailResponseSchema
>;

export const NameSanitizationRuleDetailResponseSchema = z.object({
    success: z.boolean(),
    data: NameSanitizationRuleSchema,
});
export type NameSanitizationRuleDetailResponse = z.infer<
    typeof NameSanitizationRuleDetailResponseSchema
>;

export const PreviewNameSanitizationDetailResponseSchema = z.object({
    success: z.boolean(),
    data: PreviewNameSanitizationResponseSchema,
});
export type PreviewNameSanitizationDetailResponse = z.infer<
    typeof PreviewNameSanitizationDetailResponseSchema
>;

export const ClearNameSanitizationRulesDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ClearNameSanitizationRulesResponseSchema,
});
export type ClearNameSanitizationRulesDetailResponse = z.infer<
    typeof ClearNameSanitizationRulesDetailResponseSchema
>;

export const RunNameCleaningDetailResponseSchema = z.object({
    success: z.boolean(),
    data: RunNameCleaningResponseSchema,
});
export type RunNameCleaningDetailResponse = z.infer<typeof RunNameCleaningDetailResponseSchema>;
